import type { Scene } from '../../scene/sceneState.js';
import type { AppState } from '../../state/appState.js';
import type { CommandBatch } from '../../editor/commandHistory.js';
import type { EditorCommands } from '../../editor/commands.js';
import {
  createPropRow,
  createSectionHeader,
} from './widgets.js';
import { renderLayerSlotInspector, type SlotInspectorDeps } from './slotInspector.js';
import { renderNodeTransformProps } from './transformProps.js';
import { renderLeafProps } from './leafProps.js';
import { renderFrameProps, type FramePropsDeps } from './frameProps.js';
import { renderDataBindings } from '../dataBindings.js';

export interface SceneTreePropertiesDeps extends FramePropsDeps {
  appState: AppState;
  commandBatch: CommandBatch;
  commands: FramePropsDeps['commands'] & Pick<EditorCommands, 'renameComponent' | 'renameSceneNode' | 'setParamValue' | 'setNodeParamOverride' | 'setDataOwner' | 'setSceneEvents' | 'setSceneDataContext'>;
  editorNavigation: SlotInspectorDeps['editorNavigation'];
  editorSidebar: SlotInspectorDeps['editorSidebar'];
  renderSceneTree: () => void;
  scheduleRender: () => void;
  selection: SlotInspectorDeps['selection'];
  renderExternalInspector?: (host: HTMLElement) => boolean;
}

export function renderPropertiesPanel(
  deps: SceneTreePropertiesDeps,
  parent: HTMLElement,
  activeScene: Scene,
): void {
  const panel = document.createElement('div');
  panel.className = 'scene-props';
  const { appState, commands } = deps;

  const selectedSceneNodeId = deps.selection.getSelectedSceneNodeId();
  const selectedVirtualTarget = deps.selection.getSelectedVirtualTarget();

  if (selectedVirtualTarget === 'scene' && !appState.activeComponentId) {
    renderVirtualTargetHeader(panel, '◈', 'Scene', 'scene');
    renderDataBindings(panel, appState, commands, { type: 'document' }, appState.data, deps.renderSceneTree);
    parent.appendChild(panel);
    return;
  }

  if (selectedVirtualTarget === 'component' && appState.activeComponentId) {
    const component = appState.componentLibrary[appState.activeComponentId];
    if (!component) {
      renderEmptyInspector(panel);
      parent.appendChild(panel);
      return;
    }

    renderVirtualTargetHeader(panel, '◆', component.name, 'component');

    const nameRow = createPropRow('componentName');
    const nameInput = document.createElement('input');
    nameInput.type = 'text';
    nameInput.className = 'scene-prop-input';
    nameInput.value = component.name;
    nameInput.addEventListener('change', () => {
      const next = nameInput.value.trim();
      if (!next || next === component.name) {
        nameInput.value = component.name;
        return;
      }
      commands.renameComponent(component.id, next);
      deps.renderSceneTree();
    });
    nameRow.appendChild(nameInput);
    panel.appendChild(nameRow);
    parent.appendChild(panel);
    return;
  }

  if (!selectedSceneNodeId) {
    renderEmptyInspector(panel);
    parent.appendChild(panel);
    return;
  }
  const node = activeScene.nodes[selectedSceneNodeId];
  if (!node) {
    renderEmptyInspector(panel);
    parent.appendChild(panel);
    return;
  }

  renderVirtualTargetHeader(
    panel,
    node.kind === 'frame' ? '▾' : node.kind === 'layer' ? '●' : '◆',
    node.name,
    node.kind,
  );

  if (node.kind === 'instance') {
    const component = appState.componentLibrary[node.componentId];
    if (component) {
      const componentNameRow = createPropRow('componentName');
      const componentNameValue = document.createElement('div');
      componentNameValue.className = 'scene-prop-value';
      componentNameValue.textContent = component.name;
      componentNameRow.appendChild(componentNameValue);
      panel.appendChild(componentNameRow);
    }
  }

  const nameRow = createPropRow(
    node.kind === 'instance'
      ? 'instanceName'
      : node.kind === 'layer'
        ? 'layerName'
        : 'name',
  );
  const nameInput = document.createElement('input');
  nameInput.type = 'text';
  nameInput.className = 'scene-prop-input';
  nameInput.value = node.name;
  nameInput.addEventListener('change', () => {
    const next = nameInput.value.trim();
    if (!next) {
      nameInput.value = node.name;
      return;
    }
    if (next === node.name) return;
    commands.renameSceneNode(selectedSceneNodeId, next);
    deps.renderSceneTree();
  });
  nameRow.appendChild(nameInput);
  panel.appendChild(nameRow);
  renderSceneDataContext(panel, appState, commands, node.id, node.dataContext, deps.renderSceneTree);
  renderDataBindings(panel, appState, commands, { type: 'scene', nodeId: node.id }, node.behavior, deps.renderSceneTree);
  const eventHeader = createSectionHeader('behavior'); eventHeader.textContent = 'EVENTS'; panel.appendChild(eventHeader);
  const activateRow = createPropRow('source'); activateRow.firstElementChild!.textContent = 'Activate';
  const activate = document.createElement('select'); activate.className = 'scene-prop-input';
  activate.appendChild(new Option('None', ''));
  for (const [graphId, graph] of Object.entries(appState.eventGraphs).sort(([a], [b]) => a.localeCompare(b))) {
    const events = (graph.nodes as Array<{ type: string; data: Record<string, unknown> }>).filter(candidate => candidate.type === 'OnEvent').map(candidate => String(candidate.data.name ?? ''));
    for (const event of events) activate.appendChild(new Option(`${graphId} / ${event}`, `${graphId}:${event}`));
  }
  activate.value = node.events?.activate ? `${node.events.activate.graph}:${node.events.activate.event}` : '';
  activate.addEventListener('change', () => { const [graph, event] = activate.value.split(':'); commands.setSceneEvents(node.id, graph && event ? { activate: { graph, event } } : null); deps.renderSceneTree(); });
  activateRow.appendChild(activate); panel.appendChild(activateRow);

  if (node.kind === 'frame') {
    renderNodeTransformProps(deps, panel, node, activeScene);
    renderFrameProps(deps, panel, node, activeScene);
  }

  if (node.kind === 'instance' || node.kind === 'layer') {
    if (node.kind === 'layer') {
      const ownerComponentId = appState.activeComponentId ?? null;
      panel.appendChild(createSectionHeader('geometry'));
      renderLayerSlotInspector(deps, panel, ownerComponentId, node.id, 'geometry', node.layerId);
      panel.appendChild(createSectionHeader('material'));
      renderLayerSlotInspector(deps, panel, ownerComponentId, node.id, 'material', node.layerId);
    }

    renderNodeTransformProps(deps, panel, node, activeScene);
    renderLeafProps(panel, node, activeScene, deps);
  }

  parent.appendChild(panel);
}

function renderSceneDataContext(
  panel: HTMLElement,
  appState: AppState,
  commands: Pick<EditorCommands, 'setSceneDataContext'>,
  nodeId: string,
  context: import('../../graph/dataGraph.js').SceneDataContext | undefined,
  render: () => void,
): void {
  const header = createSectionHeader('behavior');
  header.textContent = 'DATA CONTEXT';
  panel.appendChild(header);

  const sourceRow = createPropRow('source');
  sourceRow.firstElementChild!.textContent = 'Source';
  const source = document.createElement('select');
  source.className = 'scene-prop-input';
  source.appendChild(new Option('Inherit / None', ''));
  for (const id of Object.keys(appState.dataSources).sort()) source.appendChild(new Option(id, id));
  source.value = context?.source ?? '';
  source.addEventListener('change', () => {
    if (!source.value) commands.setSceneDataContext(nodeId, null);
    else {
      const keyType = appState.dataSources[source.value]?.keyType ?? 'string';
      const key = keyType === 'number' ? 0 : keyType === 'bool' ? false : '';
      commands.setSceneDataContext(nodeId, { source: source.value, key });
    }
    render();
  });
  sourceRow.appendChild(source);
  panel.appendChild(sourceRow);

  if (!context) return;
  const keyType = appState.dataSources[context.source]?.keyType ?? 'string';
  const keyRow = createPropRow('source');
  keyRow.firstElementChild!.textContent = 'Key';
  const key = document.createElement(keyType === 'bool' ? 'select' : 'input');
  key.className = 'scene-prop-input';
  if (key instanceof HTMLSelectElement) {
    key.append(new Option('False', 'false'), new Option('True', 'true'));
    key.value = String(context.key);
  } else {
    key.type = keyType === 'number' ? 'number' : 'text';
    key.value = String(context.key);
  }
  key.addEventListener('change', () => {
    const raw = key.value;
    const value = keyType === 'number' ? Number(raw) : keyType === 'bool' ? raw === 'true' : raw;
    if (keyType === 'number' && !Number.isFinite(value)) return;
    commands.setSceneDataContext(nodeId, { source: context.source, key: value });
    render();
  });
  keyRow.appendChild(key);
  panel.appendChild(keyRow);
}

export function renderPropertiesInspector(
  deps: SceneTreePropertiesDeps,
  activeScene: Scene,
): void {
  const host = document.getElementById('right-inspector-content');
  if (!host) return;
  if (deps.renderExternalInspector?.(host)) return;
  host.innerHTML = '';
  renderPropertiesPanel(deps, host, activeScene);
}

function renderEmptyInspector(panel: HTMLElement): void {
  const empty = document.createElement('div');
  empty.className = 'right-inspector-empty';
  empty.textContent = 'Select a scene target';
  panel.appendChild(empty);
}

function renderVirtualTargetHeader(panel: HTMLElement, icon: string, label: string, kind: string): void {
  const header = document.createElement('div');
  header.className = 'scene-props-header';

  const headerIcon = document.createElement('span');
  headerIcon.className = 'scene-props-header-icon';
  headerIcon.textContent = icon;
  header.appendChild(headerIcon);

  const headerLabel = document.createElement('span');
  headerLabel.textContent = label;
  header.appendChild(headerLabel);

  const headerKind = document.createElement('span');
  headerKind.className = 'scene-props-header-kind';
  headerKind.textContent = kind;
  header.appendChild(headerKind);

  panel.appendChild(header);
}
